/*------------------------------------------------------------------------------
   About      : Pure permission-check logic for the generic RBAC layer (v2)

   No React / RunContext dependencies — trivially unit-testable. UI consumes
   this through the usePermissions hook / PermissionGate / RequirePermission.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { BffFeatureAction,
         BffFeatureDomain }  from './PermissionCatalog'
import { UiPageId, UiCtaId } from './UiRegistry'

/** Every `ui.page.*` id and every nested `ui.cta.*.*` id — see UiRegistry.ts. */
type UiAction = UiPageId | UiCtaId

/**
 * A declarative access requirement:
 * - a single action string — a proxypulse BFF_FEATURES.* endpoint id or a
 *   FE-owned UI_PAGES.*.id / .ctas.*.id (one merged granted set, see
 *   usePermissions.ts)
 * - anyOf : at least one of the listed actions
 * - allOf : every listed action
 * - a whole catalog domain (e.g. BFF_FEATURES.CX) : at least one of the
 *   actions listed in that domain
 */
export type PermissionRequirement =
  | BffFeatureAction | UiAction
  | { anyOf : readonly (BffFeatureAction | UiAction)[] }
  | { allOf : readonly (BffFeatureAction | UiAction)[] }
  | BffFeatureDomain

export function checkAccess(granted : ReadonlySet<string>,
                            req     : PermissionRequirement) : boolean {

  if (typeof req === 'string') return granted.has(req)

  if ('anyOf' in req) return req.anyOf.some(permission => granted.has(permission))

  if ('allOf' in req) return req.allOf.every(permission => granted.has(permission))

  // Whole catalog domain (e.g. BFF_FEATURES.CX) — any listed action
  return Object.values(req).some(permission => granted.has(permission))
}

/**
 * Dev-only escape hatch: `localStorage.setItem('quill_perm_bypass', '1')`.
 * The NODE_ENV guard makes this dead code in production builds.
 */
export function isDevPermissionBypass() : boolean {
  return process.env.NODE_ENV === 'development'
    && localStorage.getItem('quill_perm_bypass') === '1'
}
